import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faCopy, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { logisticsAPI } from '../services/api';
import './DeliveryCodeDisplay.css';

const DeliveryCodeDisplay = ({ orderId, type = 'delivery' }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadDeliveryCodes();
  }, [orderId]);

  const loadDeliveryCodes = async () => {
    setLoadError(false);
    try {
      const response = await logisticsAPI.getDeliveryCodes(orderId);
      const codes = response.data || response;
      const deliveryCode = type === 'pickup'
        ? (codes.pickupCode || codes.pickup_code)
        : (codes.deliveryCode || codes.delivery_code);

      if (!deliveryCode) throw new Error('No code returned');
      setCode(deliveryCode);
    } catch (error) {
      // A random fabricated 6-digit code used to be shown here on ANY
      // failure, displayed with the exact same "show this code" copy as a
      // real one — a confidently-wrong code is worse than an honest error,
      // since whoever it's shown to would act on it as if it were genuine.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="delivery-code-container loading">
        <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
        <span className="loading-text">Loading code...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="delivery-code-container">
        <div className="delivery-code-header">
          <FontAwesomeIcon icon={faKey} className="key-icon" />
          <h3 className="delivery-code-title">
            {type === 'pickup' ? 'Pickup' : 'Delivery'} Code
          </h3>
        </div>
        <p className="delivery-code-description">
          Couldn't load your {type} code right now. Please refresh, or contact support if this keeps happening.
        </p>
        <button className="copy-button" onClick={loadDeliveryCodes} title="Retry">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="delivery-code-container">
      <div className="delivery-code-header">
        <FontAwesomeIcon icon={faKey} className="key-icon" />
        <h3 className="delivery-code-title">
          {type === 'pickup' ? 'Pickup' : 'Delivery'} Code
        </h3>
      </div>

      <p className="delivery-code-description">
        Show this code to the {type === 'pickup' ? 'seller' : 'delivery person'}
      </p>

      <div className="delivery-code-display">
        <span className="code-text">{code}</span>
        <button
          className={`copy-button ${copied ? 'copied' : ''}`}
          onClick={copyToClipboard}
          title="Copy code"
        >
          <FontAwesomeIcon icon={faCopy} />
          {copied && <span className="copy-feedback">Copied!</span>}
        </button>
      </div>

      <div className="delivery-code-note">
        <i className="fas fa-info-circle"></i>
        <span>This code expires after successful {type}</span>
      </div>
    </div>
  );
};

export default DeliveryCodeDisplay;
